import React from 'react';

import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {Route, useRouteMatch} from 'react-router-dom';
import {toast} from 'react-toastify';

import {downloadDocumentFile} from 'api/documents';
import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {ReactComponent as DownloadIcon} from 'assets/svg/download2.svg';
import {Icon} from 'components/ui-kit/Icon';
import {
    ContentColumns,
    TitleText,
    Title,
    Id,
    CopyButton,
    StatusWrapper,
    TitleWrapper,
} from 'components/ui-kit/PageComponents';
import {RouterSwitch, RouterTab, RouterTabList} from 'components/ui-kit/RouterTabs';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {colors} from 'theme';
import {DropOutReport} from 'types/documents';
import {useClipboard} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {Products} from './Products';
import {
    AttachmentButton,
    AttachmentName,
    AttachmentsList,
    AttachmentsTitle,
    AttachmentsWrapper,
    Error,
    ErrorsWrapper,
} from './styled';

interface OrdersDetailPageProps {
    data: DropOutReport;
}

export function convertData(data: DropOutReport, t: TFunction): DropOutReport {
    return {
        reportId: handleNil(data?.reportId),
        createdAt: handleNil(data?.createdAt, (value: string) =>
            formatDate(value, 'dd.MM.yyyy, HH:mm'),
        ),
        reportType: data?.reportType,
        status: handleNil(data?.status, (value: string) =>
            t(`dictionaries.documents.status.${value}`),
        ),
        errors: data.errors,
        reportInfo: {
            // t('pages.documents.details.DropOut.reason')
            reason: handleNil(data?.reportInfo?.reason, (value: string) =>
                t(`dictionaries.documents.DropOut.reasons.${value}`),
            ),
            // t('pages.documents.details.DropOut.documentDate')
            createdAt: handleNil(data?.reportInfo?.createdAt, (value: string) =>
                formatDate(value, 'dd.MM.yyyy, HH:mm'),
            ),
            attachment: data?.reportInfo?.attachment ?? [],
        },
    };
}

const getFileName = (attachment: string) => {
    const normalizedPath = attachment.replace(/\\/g, '/');
    return normalizedPath.split('/').pop() || attachment;
};

export const DropOut = ({data}: OrdersDetailPageProps) => {
    const {t} = useTranslation();
    const {path, url} = useRouteMatch();
    const copy = useClipboard();
    const [downloadingFile, setDownloadingFile] = React.useState<string | null>(null);

    const convertedData = React.useMemo(() => convertData(data, t), [data, t]);

    const copyCode = () => {
        copy(convertedData.reportId);
        toast.success(t('pages.documents.details.idCopied'));
    };

    const downloadAttachment = async (attachment: string) => {
        const fileName = getFileName(attachment);

        try {
            setDownloadingFile(attachment);
            const blob = await downloadDocumentFile(convertedData.reportId, fileName);
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = objectUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            toast.error(t('pages.documents.details.DropOut.downloadError'));
        } finally {
            setDownloadingFile(null);
        }
    };

    return (
        <>
            <TitleWrapper>
                <TitleText>
                    <Title>
                        {t('pages.documents.details.DropOut.DropOutId')}{' '}
                        <Id>{convertedData.reportId}</Id>
                    </Title>
                    <CopyButton
                        icon={
                            <Icon
                                source={CopyIcon}
                                width={15}
                                height={15}
                                fillColor={colors.brandPrimary}
                            />
                        }
                        onClick={copyCode}
                        hoverIndication={true}
                        label={t('pages.documents.details.copyId')}
                        labelPlace={'bottom'}
                    />
                </TitleText>
                <StatusWrapper color={DOCUMENT_STATUS_COLORS[data.status ?? 'DEFAULT']}>
                    {convertedData.status}
                </StatusWrapper>
            </TitleWrapper>

            <RouterTabList baseUrl={url}>
                <RouterTab url="">{t('pages.documents.detail.commonInfo')}</RouterTab>
                <RouterTab url="products">{t('pages.documents.detail.products')}</RouterTab>
            </RouterTabList>

            <RouterSwitch rootPath={path}>
                <Route path={`${path}`} exact>
                    <ErrorsWrapper>
                        {convertedData.errors?.map((err, index) => (
                            <Error key={index}>{err.message ?? ''}</Error>
                        ))}
                    </ErrorsWrapper>
                    <ContentColumns
                        data={[
                            {
                                reason: convertedData.reportInfo.reason,
                            },
                            {
                                documentDate: convertedData.createdAt,
                            },
                        ]}
                        tNameSpace="pages.documents.details.DropOut"
                    />
                    {!!convertedData.reportInfo.attachment?.length && (
                        <AttachmentsWrapper>
                            <AttachmentsTitle>
                                {t('pages.documents.details.DropOut.attachments')}
                            </AttachmentsTitle>
                            <AttachmentsList>
                                {convertedData.reportInfo.attachment.map((attachment) => (
                                    <AttachmentButton
                                        key={attachment}
                                        type="button"
                                        disabled={downloadingFile !== null}
                                        onClick={() => downloadAttachment(attachment)}
                                    >
                                        <AttachmentName>{getFileName(attachment)}</AttachmentName>
                                        <Icon
                                            source={DownloadIcon}
                                            width={16}
                                            height={16}
                                            strokeColor={colors.brandPrimary}
                                            fillColor="transparent"
                                        />
                                    </AttachmentButton>
                                ))}
                            </AttachmentsList>
                        </AttachmentsWrapper>
                    )}
                </Route>
                <Route path={`${path}/products`} exact>
                    <Products reportId={convertedData.reportId} />
                </Route>
            </RouterSwitch>
        </>
    );
};
